/**
 * Format price from cents to display string using Shopify's money format
 */
export function formatPrice(cents) {
  const moneyFormat = window.moneyFormat || '${{amount_no_decimals}}';
  return window.formatMoney ? window.formatMoney(cents, moneyFormat) : `$${Math.round(cents / 100)}`;
}

/**
 * Format price with "from" prefix using Shopify's money format
 */
export function formatFromPrice(cents) {
  const moneyFormat = window.moneyFormat || '${{amount_no_decimals}}';
  const formattedPrice = window.formatMoney ? window.formatMoney(cents, moneyFormat) : `$${Math.round(cents / 100)}`;
  return `from ${formattedPrice}`;
}

/**
 * Calculate price for a variant with member discount
 */
export function calculateVariantPrice(variant, memberDiscount = 0) {
  if (!variant) return 0;

  const basePrice = parseFloat(variant.price || 0);

  const isMember = typeof Alpine !== 'undefined' && Alpine.store('membership')?.isLoggedIn;

  if (isMember && variant.compare_at_price) {
    return parseFloat(variant.compare_at_price) * 100;
  }

  if (memberDiscount > 0) {
    return Math.round(basePrice * (1 - memberDiscount / 100) * 100);
  }

  return basePrice * 100;
}

/**
 * Calculate total price for multiple artworks
 */
export function calculateTotalPrice(artworks, config = null) {
  if (!artworks || !Array.isArray(artworks)) return 0;

  return artworks
    .filter(artwork => artwork && artwork.id)
    .reduce((total, artwork) => {
      const variant = artwork.selected_variant || artwork.current_variant;
      const price = variant ? parseFloat(variant.price || 0) : (artwork.base_price || 0);
      return total + (price * 100);
    }, 0);
}

/**
 * Get minimum price from array of variants
 */
export function getMinimumPrice(variants) {
  if (!variants || variants.length === 0) return 0;

  const isMember = typeof Alpine !== 'undefined' && Alpine.store('membership')?.isLoggedIn;

  const prices = variants
    .filter(v => v && v.available)
    .map(v => {
      if (isMember && v.compare_at_price) {
        const memberPrice = parseFloat(v.compare_at_price);
        return memberPrice > 0 ? memberPrice : parseFloat(v.price || 0);
      }
      return parseFloat(v.price || 0);
    })
    .filter(p => p > 0);

  return prices.length > 0 ? Math.min(...prices) * 100 : 0;
}

/**
 * Get price range for a set of variants
 */
export function getPriceRange(variants) {
  if (!variants || variants.length === 0) {
    return { min: 0, max: 0, average: 0 };
  }

  const prices = variants
    .filter(v => v && v.available)
    .map(v => parseFloat(v.price || 0))
    .filter(p => p > 0);

  if (prices.length === 0) {
    return { min: 0, max: 0, average: 0 };
  }

  return {
    min: Math.min(...prices) * 100,
    max: Math.max(...prices) * 100,
    average: (prices.reduce((sum, p) => sum + p, 0) / prices.length) * 100
  };
}

/**
 * Get the appropriate price for a variant based on member login status
 */
export function getMemberPrice(variant) {
  if (!variant) return 0;

  const isMember = typeof Alpine !== 'undefined' && Alpine.store('membership')?.isLoggedIn;

  if (isMember && variant.compare_at_price) {
    const memberPrice = parseFloat(variant.compare_at_price);
    if (memberPrice > 0) {
      return memberPrice * 100;
    }
  }

  const regularPrice = parseFloat(variant.price || 0);
  return regularPrice * 100;
}

/**
 * Calculate member price with discount
 */
export function calculateMemberPrice(price, discountPercentage) {
  if (!discountPercentage || discountPercentage <= 0) return price;
  return Math.round(price * (1 - discountPercentage / 100));
}

/**
 * Validate that a price value is in the expected cents format
 */
export function validatePriceFormat(price, context = 'price') {
  if (typeof price !== 'number' || isNaN(price)) {
    console.warn(`Invalid price format in ${context}: expected number, got ${typeof price}`);
    return false;
  }

  if (price > 0 && price < 1) {
    console.warn(`Suspiciously low price value in ${context}: ${price} (expected dollars from Shopify API)`);
    return false;
  }

  return true;
}

/**
 * Safely convert price to cents if needed
 */
export function ensurePriceInCents(price, context = 'price') {
  if (typeof price !== 'number' || isNaN(price)) {
    console.warn(`Invalid price in ${context}: ${price}`);
    return 0;
  }

  if (price > 0) {
    return Math.round(price * 100);
  }

  return Math.round(price);
}

